import { useEffect, useRef, useState } from "react"

import { Viewer as ViewerClass, checkWebGPUAvailability } from "@cgtrader/cgt-viewer"

const ROTATION_SPEED_MIN = -100
const ROTATION_SPEED_MAX = 100

// Exposes a console-friendly helper so non-developers can tune the model's
// auto-rotation speed from the browser console: setModelRotationSpeed(50).
function exposeRotationSpeedControl(instance) {
  if (typeof window === "undefined") {
    return
  }

  window.setModelRotationSpeed = (speed) => {
    const value = Number(speed)
    if (!Number.isFinite(value)) {
      // eslint-disable-next-line no-console
      console.warn("setModelRotationSpeed expects a number between -100 and 100, e.g. setModelRotationSpeed(50)")
      return
    }

    const clamped = Math.min(ROTATION_SPEED_MAX, Math.max(ROTATION_SPEED_MIN, value))
    instance.setCameraSettings({ autoRotate: clamped !== 0, autoRotateSpeed: clamped })
    // eslint-disable-next-line no-console
    console.log(clamped === 0 ? "Model rotation stopped." : `Model rotation speed set to ${clamped}.`)
  }
}

function removeRotationSpeedControl() {
  if (typeof window !== "undefined") {
    delete window.setModelRotationSpeed
  }
}

export function useViewer(canvasRef, options) {
  const [viewer, setViewer] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }

    const isFirefox = typeof navigator !== "undefined"
      && /firefox/i.test(navigator.userAgent)

    let disposed = false
    let instance = null;

    (async () => {
      try {
        const gpu = await checkWebGPUAvailability()
        const opts = optionsRef.current
        // Firefox's WebGPU surface reconfiguration wedges the tab when the canvas
        // resizes continuously (e.g. during the sidebar width transition).
        const resolvedBackend = opts.backend
          ?? (!isFirefox && gpu.available ? "webgpu" : "webgl")

        instance = await ViewerClass.create(canvas, {
          dracoPath: opts.dracoPath ?? "/draco/gltf/",
          backend: resolvedBackend,
          environmentPresets: opts.environmentPresets,
          autoStart: opts.autoStart,
        })

        if (disposed) {
          instance.dispose()
          return
        }

        setViewer(instance)
        exposeRotationSpeedControl(instance)
      } catch (err) {
        if (!disposed) {
          setError(err instanceof Error ? err.message : String(err))
        }
      } finally {
        if (!disposed) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      disposed = true
      removeRotationSpeedControl()
      if (instance && !instance.isDisposed) {
        instance.dispose()
      }
      setViewer(null)
      setIsLoading(true)
      setError(null)
    }
  }, [canvasRef])

  return { viewer, isLoading, error }
}

